import { dev } from '$app/environment';
import { type BreakpointKey, BREAKPOINTS } from '$lib/constants';
import { resizeUrl, mapRange, clamp } from '$lib/utils';
import type { ImageProcessingKey } from '$lib/utils/images';

export type ImageSizes = {
  [key in BreakpointKey]?: number;
};

/** we do not want to be resizing svgs */
const resizableImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif'];

/**
 * @description Sorts the sizes of an image from smallest to largest and returns an array of tuples.
 * @param sizes an object of ImageSizes like: `{ xs: 100, sm: 200, md: 300, lg: 400, xl: 500 }`
 */
export const sortSizes = (imageSizes: ImageSizes): [BreakpointKey, number][] => {
  return Object.entries(imageSizes).sort(([, v1], [, v2]) => v1 - v2) as [BreakpointKey, number][];
};

/**
 * @decription Checks if the image is resizable based on the extension.
 * @param src
 */
export const isResizable = (src: string) => {
  return !src.includes('resize.metafy') && resizableImageExtensions.some(ext => src.endsWith(ext));
};

/**
 * @description Checks if the image is a staging asset, and if we're in dev environment
 * because the resizer doesn't work with staging assets in localhost.
 * @param src
 */
export const isStagingAsset = (src: string) => {
  const isDev = import.meta.env.VITEST === 'true' || dev;
  const stagingAsset = new URL(src).host.includes('staging');
  return isDev && stagingAsset;
};

export const mapQuality = (size: number, quality: { min: number; max: number }) => {
  // get the quality values for a given size. they are reverse mapped along a linear scale
  // from 0 to 1200 so that the smaller images are have higher quality
  // ~ quality should be an integer value
  return clamp(+Math.floor(mapRange(size, 0, 1200, quality.max, quality.min)), quality.min, quality.max);
};

export const mapSharpen = (size: number, sharpen: { min: number; max: number }) => {
  // get the sharpen values for a given size. they are mapped along a linear scale
  // from 1200 to 0 so that the smaller images are less sharpened
  // ~ sharpen can be a float value
  return clamp(+mapRange(size, 0, 1200, sharpen.min, sharpen.max).toFixed(2), sharpen.min, sharpen.max);
};

/**
 *
 * @param sortedSizes a sorted array of tuples
 */
export const getSrcSet = (src, sortedSizes: [BreakpointKey, number][], imageOptions) => {
  const { fit, gravity, sharpen, quality, format, aspect } = imageOptions;
  const hasSizes = sortedSizes.length > 0;
  if (!src || !hasSizes || !isResizable(src || '') || isStagingAsset(src || '')) {
    return [];
  }

  return sortedSizes.map(([key, size], index) => {
    let imageSource = { srcset: '', media: '' };
    const options1x: Partial<Record<ImageProcessingKey, any>> = {
      fit,
      format,
      gravity,
      width: size,
      quality: mapQuality(size, quality),
      sharpen: mapSharpen(size, sharpen)
    };
    const options2x: Partial<Record<ImageProcessingKey, any>> = {
      ...options1x,
      dpr: 2,
      quality: mapQuality(size * 2, quality),
      sharpen: mapSharpen(size * 2, sharpen)
    };

    if (aspect) {
      // if aspect ratio is provided, calculate the height based on the aspect ratio
      // because cloudflare doesn't support aspect ratio resizing, only width and height
      options1x.height = Math.floor(size / aspect);
      options2x.height = Math.floor(size / aspect);
    }

    // create the srcset for this breakpoint, both 1x and 2x
    const src1x = `${resizeUrl(src, options1x)} ${size}w`;
    const src2x = `${resizeUrl(src, options2x)} ${size * 2}w`;

    if (index === sortedSizes.length - 1) {
      // the last item in the srcset should have no media query
      imageSource.srcset = `${src2x}, ${src1x}`;
      imageSource.media = `
        (max-width: ${BREAKPOINTS[key]}px) AND 
        (max-resolution: 140dpi) ${size * 2}px, ${size}px`;
    } else {
      imageSource.srcset = `${src1x}, ${src2x}`;
      imageSource.media = `
        (max-width: ${BREAKPOINTS[key]}px) ${size}px, 
        (max-width: ${BREAKPOINTS[key]}px) AND (max-resolution: 140dpi) ${size * 2}px
      `;
    }
    return imageSource;
  });
};

/**
 * @description returns a tiny square image with a an average color of the original image
 * @param src the image url
 * @note this doesn't work for staging assets in localhost
 */
export const getBackgroundColorImage = (src: string): string => {
  if (isStagingAsset(src)) return src;
  return resizeUrl(src, { width: 8, height: 8, blur: 8, fit: 'cover', gravity: 'center', quality: 60, format: 'webp' });
};

/**
 * @description returns a tiny version of the image with a blur effect
 * @param src the image url
 * @param size the size of the blurred image
 * @note this doesn't work for staging assets in localhost
 */
export const getBlurImage = (
  src: string,
  width = 12,
  blur?: number,
  fit = 'cover',
  gravity = 'center',
  quality = 70
): string => {
  if (isStagingAsset(src)) return src;
  if (!src) return '/fallback.png';
  return resizeUrl(src, {
    width,
    fit,
    gravity,
    quality,
    blur: blur ?? Math.ceil(width / 4),
    format: 'webp'
  });
};
